import type { ComponentProps } from 'react';

import { cn } from '../lib/cn';

export type LogoVariant = 'horizontal' | 'vertical' | 'symbol' | 'wordmark';
export type LogoSize = 'sm' | 'md' | 'lg';

export interface LogoProps extends Omit<
  ComponentProps<'svg'>,
  'children' | 'color' | 'fill' | 'viewBox'
> {
  /** 기본 'horizontal'(가로 락업). 'wordmark'는 심볼이 이미 같은 화면에 있을 때만. */
  variant?: LogoVariant;
  /** 높이 단계 — 가로 락업 24 · 28 · 32px(앱 헤더는 lg). 22px 미만이 필요하면 'symbol'을 쓴다. */
  size?: LogoSize;
  /** 'mono'는 currentColor 한 색이고 접힘은 그늘 대신 틈으로 남는다. */
  tone?: 'color' | 'mono';
  /** 바탕을 고정할 때만 준다. 주지 않으면 테마(`data-theme`)를 따른다. */
  surface?: 'light' | 'dark';
}

/** 아웃라인 원본 — IBM Plex Sans KR SemiBold 글리프, fl 합자. 글꼴로 다시 조판하지 않는다(D1 §7-1). */
const WORDMARK_PATH =
  'M369.6 328.8V49.6H422.4V282H532.4V328.8ZM715.2 328.8Q698.4 328.8 688.6 319Q678.8 309.2 676.4 292.8H674Q668.8 313.2 652.8 323.4Q636.8 333.6 613.2 333.6Q581.2 333.6 564 316.8Q546.8 300 546.8 272Q546.8 239.6 570 223.8Q593.2 208 636 208H671.6V192.8Q671.6 175.2 662.4 165.6Q653.2 156 632.8 156Q614.8 156 603.8 163.8Q592.8 171.6 585.2 182.4L554.8 155.2Q566.4 137.2 585.6 126.2Q604.8 115.2 636.4 115.2Q678.8 115.2 700.8 134.4Q722.8 153.6 722.8 189.6V288H743.6V328.8ZM630.4 296.4Q647.6 296.4 659.6 288.8Q671.6 281.2 671.6 266.4V238.8H638.8Q598.8 238.8 598.8 264.4V271.2Q598.8 284 607 290.2Q615.2 296.4 630.4 296.4ZM780.4 32.8H831.6V154.4H833.2Q838.8 136.4 854.4 125.8Q870 115.2 890.8 115.2Q930.8 115.2 951.8 143.4Q972.8 171.6 972.8 224Q972.8 276.8 951.8 305.2Q930.8 333.6 890.8 333.6Q870 333.6 854.6 322.8Q839.2 312 833.2 294H831.6V328.8H780.4ZM874.4 291.2Q894.4 291.2 906.8 278Q919.2 264.8 919.2 242.4V206.4Q919.2 184 906.8 170.6Q894.4 157.2 874.4 157.2Q856 157.2 843.8 166.4Q831.6 175.6 831.6 190.8V257.2Q831.6 273.6 843.8 282.4Q856 291.2 874.4 291.2ZM1023.6 328.8V90Q1023.6 62.4 1038.2 47.6Q1052.8 32.8 1081.2 32.8H1199.6V288H1227.2V328.8H1199.6Q1173.2 328.8 1160.8 315.6Q1148.4 302.4 1148.4 278.4V73.6H1074.8V328.8ZM992.8 160.4V120H1023.6V160.4ZM1074.8 160.4V120H1117.6V160.4ZM1344 333.6Q1321.6 333.6 1303.8 326Q1286 318.4 1273.4 304Q1260.8 289.6 1254 269.4Q1247.2 249.2 1247.2 224Q1247.2 198.8 1254 178.8Q1260.8 158.8 1273.4 144.6Q1286 130.4 1303.8 122.8Q1321.6 115.2 1344 115.2Q1366.4 115.2 1384.4 122.8Q1402.4 130.4 1415 144.6Q1427.6 158.8 1434.4 178.8Q1441.2 198.8 1441.2 224Q1441.2 249.2 1434.4 269.4Q1427.6 289.6 1415 304Q1402.4 318.4 1384.4 326Q1366.4 333.6 1344 333.6ZM1344 292.4Q1364.4 292.4 1376 280Q1387.6 267.6 1387.6 243.6V204.8Q1387.6 181.2 1376 168.8Q1364.4 156.4 1344 156.4Q1324 156.4 1312.4 168.8Q1300.8 181.2 1300.8 204.8V243.6Q1300.8 267.6 1312.4 280Q1324 292.4 1344 292.4ZM1457.6 120H1506.8L1528 206L1544.4 277.6H1546L1565.2 206L1589.6 120H1636L1661.2 206L1680.8 277.6H1682.4L1698.8 206L1719.6 120H1767.2L1710 328.8H1654.8L1628 236.8L1612.4 180H1611.2L1596 236.8L1569.6 328.8H1515.2Z';

const SYMBOL = {
  face: 'M0 0H132V259H0Z',
  leg: 'M132 259H264V376H132Z',
  fold: 'M0 259H132V376Z',
  monoFace: 'M0 0H132V245.8H0Z',
  monoLeg: 'M0 259H264V376H132Z',
} as const;

const VIEW_BOX: Record<LogoVariant, string> = {
  horizontal: '0 0 1768 376',
  vertical: '0 0 1398 1287',
  symbol: '0 0 264 376',
  wordmark: '369.6 32.8 1398.4 300.8',
};

const HEIGHT: Record<LogoVariant, Record<LogoSize, string>> = {
  horizontal: { sm: 'h-6', md: 'h-7', lg: 'h-8' },
  symbol: { sm: 'h-7', md: 'h-8', lg: 'h-10' },
  wordmark: { sm: 'h-[1.2rem]', md: 'h-[1.4rem]', lg: 'h-[1.6rem]' },
  vertical: { sm: 'h-20', md: 'h-28', lg: 'h-36' },
};

const NAVY_FILL = {
  theme: { face: 'fill-logo-face', wordmark: 'fill-logo-wordmark' },
  light: { face: 'fill-brand-600', wordmark: 'fill-brand-600' },
  dark: { face: 'fill-brand-300', wordmark: 'fill-neutral-0' },
} as const;

/**
 * Labflow 로고 — D1 §7-1 · D3 §6.1. 접힌 띠 심볼 + 네이비 한 색 워드마크.
 *
 * 색은 prop으로 받지 않는다. 오렌지(accent-500 · 그늘 accent-700)는 심볼의 접힌 면 안에만 있다.
 */
export function Logo({
  variant = 'horizontal',
  size = 'md',
  tone = 'color',
  surface,
  className,
  ...props
}: LogoProps) {
  const navy = NAVY_FILL[surface ?? 'theme'];
  const mono = tone === 'mono';
  const named = !props['aria-hidden'];

  const symbol = mono ? (
    <>
      <path d={SYMBOL.monoFace} />
      <path d={SYMBOL.monoLeg} />
    </>
  ) : (
    <>
      <path className={navy.face} d={SYMBOL.face} />
      <path className="fill-accent-500" d={SYMBOL.leg} />
      <path className="fill-accent-700" d={SYMBOL.fold} />
    </>
  );
  const wordmark = <path className={mono ? undefined : navy.wordmark} d={WORDMARK_PATH} />;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={VIEW_BOX[variant]}
      role={named ? 'img' : undefined}
      aria-label={named ? 'Labflow' : undefined}
      className={cn(
        'block w-auto shrink-0',
        HEIGHT[variant][size],
        mono && 'fill-current',
        className,
      )}
      {...props}
    >
      {variant === 'symbol' ? symbol : null}
      {variant === 'wordmark' ? wordmark : null}
      {variant === 'horizontal' ? (
        <>
          {symbol}
          {wordmark}
        </>
      ) : null}
      {variant === 'vertical' ? (
        <>
          <g transform="translate(462 0) scale(2)">{symbol}</g>
          <g transform="translate(-369.6 953.2)">{wordmark}</g>
        </>
      ) : null}
    </svg>
  );
}
